/**
 * What a bookmark-list window shows — which is to say, a saved filter over the collection.
 *
 * One backing view model behind every platform's list, for the reason §05 of
 * docs/kmp-sharing-architecture.html gives: the three states a fetched list can be in —
 * waiting, empty, failed — are the part that is easy to get wrong, and getting them wrong
 * differently on three platforms is how one of them ends up showing "no bookmarks yet" for
 * what is really "the request failed".
 *
 * ── Why this one fetches and holds the whole collection ──────────────────────
 *
 * `StorageRepository.bookmarks` states the rule this depends on: the collection is written
 * from *outside* the app — a bookmarklet in another window, a phone, later an importer —
 * so there is no cache to read and no invalidation to get right. Each window asks, and
 * asks again when told to.
 *
 * Given that it is holding all of it anyway, **the filtering happens here rather than on
 * the server**, and `BookmarkFilter.kt`'s header argues that decision in full. The short
 * version is the consequence: the tag rail's counts are live against the *result set*, so
 * a tag reads 25 in an unfiltered window and 9 inside a filter — the number somebody
 * narrowing a search actually wants.
 *
 * ── What this model owns and what the workspace owns ─────────────────────────
 *
 * The **filter** is stored in the workspace, as [WindowContent.BookmarkList], because it
 * *is* the window: it survives a reload, it names the window in the sidebar, and *Save as
 * window* copies it. So this model is handed one at construction and reports every change
 * back through [onFilterChanged]; it never writes to storage itself.
 *
 * Everything else here is **the view's own state and is deliberately not persisted**:
 * which rows are expanded, which row `j`/`k` has landed on, whether the rail is expanded
 * and what is typed in its search box. None of it describes what the window *shows*, all
 * of it is where a reader has got to in a session, and persisting it would mean a
 * keystroke's worth of scrolling posting to the server. Taking the smaller option here is
 * also the reversible one: any of it can become a field on the filter later, and none of
 * them can be taken back out once a stored blob has it.
 *
 * ── Why a timer, and why it is here rather than in a view ────────────────────
 *
 * A bookmark saved a moment ago is `pending`, and its summary arrives without anybody
 * asking — which means the window has to notice. It does so by re-fetching **only while a
 * row that is waiting is in the current result set**, and stopping the instant none is.
 *
 * Not a websocket. One process serving one person does not need a second protocol to
 * deliver a sentence that arrives within a minute, and a socket would be a connection to
 * open, drop, reconnect and authenticate for a feature whose whole promise is that you can
 * look away from it.
 *
 * It is here rather than in the DOM because "is any row in the current result set waiting"
 * is a question only this model can answer: it holds the collection *and* the filter, and
 * a timer keyed on the collection alone would keep a window ticking for a pending bookmark
 * its filter hides. Being here also means every platform gets it from one place, and that
 * the one thing a timer must never do — outlive the thing it is for — is [run]'s scope
 * being cancelled rather than three separate teardown paths that each have to remember.
 *
 * @see BookmarkFilter
 * @see WindowContent.BookmarkList
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.readableMessage
import se.soderbjorn.lunapin.clientserver.AiStates
import se.soderbjorn.lunapin.clientserver.Bookmark
import se.soderbjorn.lunapin.clientserver.TagUse

/**
 * How long between two fetches while a visible row is waiting for its summary.
 *
 * Five seconds, which is a compromise between two things that are both real. The worker
 * paces itself at roughly one bookmark every three seconds, so anything much longer than
 * this would show a backfill arriving in lumps; and anything much shorter would be a
 * request per second against a server that has nothing new to say, for a window somebody
 * has stopped looking at.
 *
 * It is bounded by the *arming* rule rather than by being long: a window with nothing
 * waiting makes no requests at all, however small this is. `internal` so the test that
 * pins the arming can advance exactly this much rather than guessing.
 */
internal const val REFRESH_WHILE_AWAITING_MILLIS = 5_000L

/**
 * How many tag chips the panel draws at a time, and how many more *Show more* adds.
 *
 * Forty-eight, which is about four rows of chips in a window anybody would call wide — enough
 * that the answer is usually on screen, few enough that the panel does not become the page.
 * The number is one rather than two because the second press should feel like the first: a
 * "first 48, then 24 at a time" cap makes the reader do arithmetic to know where they are.
 */
const val RAIL_PAGE: Int = 48

/**
 * Backs one bookmark-list window.
 *
 * @param storage the session's source of truth and the only route to the server.
 * @param now epoch millis. A lambda rather than a value because a window outlives the
 *   instant it was opened at, and a lambda rather than a reached-for clock because shared
 *   code has none — see `BookmarkFilter`, whose pure functions take the time as an
 *   argument for the same reason. No default: a wrong "now" makes the *added within*
 *   filter silently empty, and a default of zero would be a wrong one.
 * @param zoneOffsetMillis the reader's offset from UTC in millis, for the day headers.
 *   Also a lambda: somebody who flies changes it without reopening the window.
 * @param initialFilter what the workspace has stored for this window.
 * @param onFilterChanged every change to the filter, as it happens. The host writes it
 *   back into the workspace — and is expected to be lazy about *how often* it writes,
 *   since this fires on every keystroke in the search field.
 * @param scope where a *Try again* is launched. Deliberately **not** the scope [run] is
 *   collected on, for [EditBookmarkBackingViewModel]'s reason: a request the user pressed a
 *   button for should finish even if they close the window a moment later, whereas a
 *   refresh nobody is looking at should not. Defaulted so no existing caller has to care.
 */
class BookmarkListBackingViewModel(
    private val storage: StorageRepository,
    private val now: () -> Long,
    private val zoneOffsetMillis: () -> Int,
    initialFilter: WindowContent.BookmarkList = WindowContent.BookmarkList(),
    private val onFilterChanged: (WindowContent.BookmarkList) -> Unit = {},
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
) {
    private val _stateFlow = MutableStateFlow(State(loading = State.Loading))

    /** The complete, render-ready description of the window. */
    val stateFlow = _stateFlow.asStateFlow()

    /**
     * Every reason to fetch, counted.
     *
     * A flow rather than a scope of this model's own, so that [run] stays the one
     * thing the platform launches and [onRefreshRequested] stays a plain non-suspending
     * intent — the four rules from §05, which every other model here follows. The value
     * is a tick rather than a `Unit` because a `StateFlow` conflates equal values, and
     * two refreshes in a row must be two fetches.
     */
    private val refreshes = MutableStateFlow(0)

    /**
     * Whether any row **in the current result set** is waiting for a summary.
     *
     * The timer's arming switch, and a `StateFlow` rather than a field because that is what
     * makes the arming reactive without a second mechanism: it conflates equal values, so a
     * window that stays busy does not restart its timer on every fetch, and it emits the
     * instant the last row settles, which is what cancels the wait.
     *
     * Recomputed in [publish] against the *result set*, not the collection. A pending
     * bookmark that this window's filter hides is not this window's business — three open
     * windows would otherwise all poll for one row that only one of them shows.
     */
    private val awaiting = MutableStateFlow(false)

    // ── What this model holds ────────────────────────────────────────────────

    /** The window's saved filter. Reported out on every change; never written from here. */
    private var filter: WindowContent.BookmarkList = initialFilter

    /** The whole collection, as fetched. Filtered on the way out, never in place. */
    private var collection: List<Bookmark> = emptyList()

    /** The account's tag vocabulary, for the search field's `#tag` menu and the rail. */
    private var vocabulary: List<TagUse> = emptyList()

    /**
     * The tags this account chose to keep within reach — *Offer by default* in settings.
     *
     * Held here to put them at the **front of the rail**, ahead of whatever the rail's sort
     * would otherwise have led with. It is the same list the save popup draws as one-press
     * chips, and reading it here is what makes that setting mean one thing rather than two:
     * a tag worth a chip when saving is the tag worth finding again first.
     *
     * Empty until the first fetch answers, and empty for good on an account that never
     * chose any — in which case every line below it is the order it always was.
     */
    private var quickTags: List<String> = emptyList()

    /** Rows the reader has opened, by id. Not persisted — see the file header. */
    private var expandedIds: Set<Long> = emptySet()

    /** Where `j`/`k` has got to, or null before anything has been focused. */
    private var focusedId: Long? = null

    /**
     * Whether the tag panel is up.
     *
     * It was *the rail, expanded* — a third band of the window's header that pushed the rows
     * down — and since LPN-66 it is a panel a surface floats over the list. The state is the
     * same boolean and the controls inside it are the same four; what changed is that opening
     * it no longer costs the rows any height. See `BookmarkListView` for the shape.
     */
    private var tagPanelOpen: Boolean = false

    /** Whether the *View* panel — sort, grouping, the two AI knobs, columns — is up. */
    private var viewPanelOpen: Boolean = false

    private var railQuery: String = ""
    private var railSort: String = RailSorts.COUNT
    private var hideEmpty: Boolean = false

    /**
     * How many of the panel's *inactive* chips are drawn, before *Show more*.
     *
     * A cap rather than a scroll bar, because *Hide empty* is not enough on its own: on an
     * unfiltered collection almost every tag is non-empty, so hiding the empties still leaves
     * hundreds, and a panel that promises the whole vocabulary is a panel nobody reads to the
     * end of. The first [RAIL_PAGE] by the chosen sort are the ones the sort was chosen for —
     * the biggest, the most recent, or the start of the alphabet — and the footer states the
     * truncation in numbers rather than hiding it.
     *
     * Reset to [RAIL_PAGE] by every control that changes *which* chips those are, so pressing
     * *Show more* four times and then typing a needle does not leave a search showing two
     * hundred results. The active chips are never subject to it; see [rail].
     */
    private var railLimit: Int = RAIL_PAGE

    /** When the collection last arrived, for the footer's *refreshed …* line. */
    private var loadedAt: Long = 0

    /** True while a *re*-fetch is open — the first fetch is [State.Loading] instead. */
    private var isRefreshing: Boolean = false

    private var failure: String? = null

    /** Whether anything has arrived yet. Distinguishes the first fetch from a refresh. */
    private var hasLoaded: Boolean = false

    /**
     * Load the collection, then keep loading it whenever asked or whenever a visible row is
     * still waiting for its summary. Called once by the platform; never returns.
     *
     * Two things run here and **the caller cancelling this cancels both**, which is the
     * whole reason the timer is a child of this function rather than a scope of its own: a
     * window that closes must stop asking, and a timer with an independent lifetime is one
     * that keeps a closed window's requests going until the page is reloaded. That is the
     * invisible kind of leak — nothing breaks, the app just gets slower and the server
     * busier the longer somebody works.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun run() {
        coroutineScope {
            launch { refreshWhileAwaiting() }
            launch { reloadOnChangesElsewhere() }
            launch { reloadOnNominationsElsewhere() }
            // Collecting a StateFlow delivers its current value first, so the initial load
            // is this and not a separate call — one path to a fetch rather than two that
            // could drift. It never completes, which is what keeps `coroutineScope` open.
            refreshes.collect { load() }
        }
    }

    /**
     * Re-read when the server says the collection changed — LPN-27's channel, arriving here.
     *
     * **A list ignores the ids and simply re-reads**, which is exactly what
     * `ServerEvent.BookmarksChanged` expects of it: this window shows a filtered view, so a
     * bookmark it is not currently showing can become one it is by having a tag added to it
     * somewhere else. Deciding "does this change concern me?" from an id would mean deciding it
     * from the filter, in the client, against a row it does not have — which is the server's
     * query, badly.
     *
     * It bumps [refreshes] directly rather than going through [onRefreshRequested], for
     * [refreshWhileAwaiting]'s reason exactly: nobody asked, so the footer must not light up
     * *REFRESHING…* because somebody saved a link on their phone.
     *
     * **This client's own writes never reach here** — `ServerEventStream` drops them by origin
     * before the repository hears about them — so saving from this window does not produce a
     * fetch that fights the one the save already made.
     *
     * The first value is skipped. A `StateFlow` delivers its current value on subscribe, and
     * `revision = 0` is "nothing has been said yet"; treating it as a change would mean every
     * window opening with two fetches, the second one immediately after the first.
     */
    private suspend fun reloadOnChangesElsewhere() {
        storage.bookmarksChangedFlow.collect { change ->
            if (change.revision == 0) return@collect
            refreshes.value += 1
        }
    }

    /**
     * Re-read when the account's typed preferences changed somewhere else.
     *
     * [load] already fetches the account's settings on every load rather than once, "because
     * this is where a change made in the settings window arrives" — and that sentence is only
     * true if something makes a load happen. There is nothing left that would: LPN-27 took the
     * refresh button off this window, so a rail that waited for the next fetch would wait for a
     * summary poll, a save somewhere else, or a page reload. On a phone the last of those is
     * not even a thing somebody can do, and the settings window they nominated the tag in is on
     * another device entirely.
     *
     * So the channel LPN-27 already carries is what closes it. `ServerEvent.AccountSettingsChanged`
     * reaches [StorageRepository.accountSettingsRevisionFlow] on every surface, and this is its
     * first reader: a nomination made in a browser reorders the rail of a list held open on a
     * phone, with nothing pressed on either side.
     *
     * A whole re-fetch for what is at most a reordering of chips, and that is the right size of
     * hammer here — the settings the rail reads are not cached anywhere this model can re-read
     * on its own, an account's preferences change a few times a year, and one extra request in
     * that moment is cheaper than a second path into the same state that could drift from
     * [load]'s. Like the other two reloaders it bumps [refreshes] directly, so the footer does
     * not announce *REFRESHING…* for something nobody here asked for, and it skips revision `0`
     * for the same reason [reloadOnChangesElsewhere] does.
     */
    private suspend fun reloadOnNominationsElsewhere() {
        storage.accountSettingsRevisionFlow.collect { revision ->
            if (revision == 0) return@collect
            refreshes.value += 1
        }
    }

    /**
     * Ask again every few seconds, but only while [awaiting] says something is coming.
     *
     * `collectLatest` is the mechanism and not a detail: it cancels the inner loop the
     * moment the flag changes, so "stop when the last row settles" is the cancellation of a
     * `delay` rather than a condition somebody has to remember to re-check. While the flag
     * stays true the flow emits nothing — a `StateFlow` conflates equal values — so the
     * loop keeps its rhythm instead of restarting on every fetch.
     *
     * It bumps [refreshes] directly rather than going through [onRefreshRequested], and
     * that is deliberate: this refresh is not something the reader asked for, so it must not
     * light the footer's *REFRESHING…* every few seconds for a window nobody has touched.
     */
    private suspend fun refreshWhileAwaiting() {
        awaiting.collectLatest { pending ->
            while (pending) {
                delay(REFRESH_WHILE_AWAITING_MILLIS)
                refreshes.value += 1
            }
        }
    }

    /**
     * Ask again.
     *
     * Worth having from the first version rather than later: the whole point of the
     * bookmarklet is that bookmarks arrive from *another window*, so a list with no way
     * to catch up is stale the first time the feature is used. Reloading the page would
     * do it too, and that is a poor answer for a window in a workspace somebody has
     * arranged.
     *
     * **The rows stay on screen while it runs.** Blanking them to a *Loading…* line was
     * the old behaviour and it is wrong for a refresh: it throws away the reader's place
     * in a list they can still see, to say something they can tell from the button.
     */
    fun onRefreshRequested() {
        if (hasLoaded) {
            isRefreshing = true
            publish()
        }
        refreshes.value += 1
    }

    /** Fetch, and turn whatever happens into a state. */
    private suspend fun load() {
        try {
            val bookmarks = storage.bookmarks()
            // The tag vocabulary is a second request and a lesser one: it feeds the `#tag`
            // menu and puts zero-count chips in the expanded rail. A failure there must not
            // take the list down with it, so it degrades to "the tags in the collection"
            // rather than to a failed window.
            val tags = runCatching { storage.tags() }.getOrDefault(vocabulary)
            // A third request, and the least of the three — it decides which chips lead the
            // rail and nothing else. Degraded exactly like the vocabulary above: keeping the
            // list we already had is a rail in the previous order, whereas failing the
            // window would be losing the bookmarks over the ordering of their tags.
            //
            // Fetched on every load rather than once, because this is where a change made in
            // the settings window arrives: both are open at the same time by design — that
            // is half of why settings is a window — and a list that only picked this up on a
            // page reload would be the setting appearing not to work.
            val chosen = runCatching { storage.accountSettings().saveQuickTags }
                .getOrDefault(quickTags)
            collection = bookmarks
            vocabulary = tags
            quickTags = chosen
            failure = null
            hasLoaded = true
            loadedAt = now()
        } catch (cancellation: CancellationException) {
            // Not a failure to report: the window closed, or the scope went with it.
            throw cancellation
        } catch (failed: Exception) {
            // A sentence, not an exception message — `readableMessage` is the same
            // mapping the sign-in surfaces use, so a server that is down says the same
            // thing here as it does there.
            failure = failed.readableMessage(COULD_NOT_LOAD)
        } finally {
            isRefreshing = false
        }
        publish()
    }

    // ── Filter intents ───────────────────────────────────────────────────────
    //
    // Every one of them ends in `changeFilter`, which is the single funnel that publishes
    // the new state and tells the host to store it. One funnel rather than one write per
    // intent is what makes "the window and the workspace agree" one claim to check.

    /** The free text in the search field, as it is typed. */
    fun onQueryChanged(query: String) {
        if (query == filter.query) return
        changeFilter(filter.copy(query = query))
    }

    /**
     * A tag was turned on — the chip's name, `⏎` in the search field, a row's own chip.
     *
     * Idempotent, and it moves a tag out of [WindowContent.BookmarkList.excludeTags] if it
     * was there: the three states are one control, so including a tag that is currently
     * excluded is a *change of state* rather than a contradiction to reject.
     */
    fun onTagIncluded(tag: String) {
        if (tag in filter.includeTags) return
        changeFilter(
            filter.copy(
                includeTags = filter.includeTags + tag,
                excludeTags = filter.excludeTags - tag,
            ),
        )
    }

    /** A tag was turned to *not this one* — the chip's minus, alt-click, or `⌥⏎`. */
    fun onTagExcluded(tag: String) {
        if (tag in filter.excludeTags) return
        changeFilter(
            filter.copy(
                includeTags = filter.includeTags - tag,
                excludeTags = filter.excludeTags + tag,
            ),
        )
    }

    /** A tag was turned off — a chip's `×`, or a third click on it. */
    fun onTagCleared(tag: String) {
        if (tag !in filter.includeTags && tag !in filter.excludeTags) return
        changeFilter(
            filter.copy(
                includeTags = filter.includeTags - tag,
                excludeTags = filter.excludeTags - tag,
            ),
        )
    }

    /**
     * One tap on a rail chip: off → included → excluded → off.
     *
     * **The three states already exist**; this is the one gesture a touch screen can express
     * them with. The web reaches *excluded* through a modifier click, which a phone has no
     * equivalent of, and excluding is the state that does the most work in practice — a tag like
     * `#read-later` covers most of a collection, so the sharpest cuts come from removing a topic
     * rather than adding one. A phone that could only include would be a phone that can only
     * make the blunt cut.
     *
     * It is here rather than in a screen because LPN-25 says so and because the alternative is
     * two screens each composing the cycle and eventually disagreeing about its order. **The web
     * could adopt it unchanged** — nothing about it is phone-shaped except the reason it was
     * needed.
     */
    fun onTagCycled(tag: String) {
        when (tag) {
            in filter.includeTags -> onTagExcluded(tag)
            in filter.excludeTags -> onTagCleared(tag)
            else -> onTagIncluded(tag)
        }
    }

    /**
     * The rail's *Clear all*.
     *
     * Tags only. The free text, the sort and the knobs are not what somebody pressing a
     * link inside the tag panel is asking to lose.
     */
    fun onTagsCleared() {
        if (filter.includeTags.isEmpty() && filter.excludeTags.isEmpty()) return
        changeFilter(filter.copy(includeTags = emptyList(), excludeTags = emptyList()))
    }

    /** The `MATCH` knob — one of [FilterMatches]. */
    fun onMatchChanged(match: String) = changeFilter(filter.copy(match = match))

    /** The `SORT` knob — one of [BookmarkSorts]. */
    fun onSortChanged(sort: String) = changeFilter(filter.copy(sort = sort))

    /** The `GROUP` knob — one of [BookmarkGroupings]. */
    fun onGroupChanged(group: String) = changeFilter(filter.copy(group = group))

    /** The `ADDED` knob — one of [AddedWithins]. */
    fun onAddedWithinChanged(addedWithin: String) =
        changeFilter(filter.copy(addedWithin = addedWithin))

    /** The `AI TAGS` knob — one of [AiTagFilters]. */
    fun onAiTagsChanged(aiTags: String) = changeFilter(filter.copy(aiTags = aiTags))

    /** The `AI SUMMARY` knob — one of [AiSummaryFilters]. */
    fun onAiSummaryChanged(aiSummary: String) = changeFilter(filter.copy(aiSummary = aiSummary))

    /** The `COLUMNS` strip — 1, 2, 3 or [BookmarkColumns.AUTO]. */
    fun onColumnsChanged(columns: Int) = changeFilter(filter.copy(columns = columns))

    /**
     * *Reset* — put every filter back to what a new window has, and leave the layout alone.
     *
     * The one control that undoes a filter nobody can remember assembling, and it is offered
     * only while there is something to undo ([State.Ready.isDirty]). It clears the tags, the
     * free text, the sort, the grouping and both AI knobs.
     *
     * **[WindowContent.BookmarkList.columns] survives it**, deliberately. Every other field
     * here decides *which rows* the window shows; the column count decides how wide a card is,
     * which is a preference about this window's shape and about the screen it is on. Somebody
     * pressing *Reset* is asking to see everything again, not to have their two-column layout
     * silently re-picked underneath them.
     */
    fun onFilterReset() {
        changeFilter(WindowContent.BookmarkList(columns = filter.columns))
    }

    /**
     * A starred filter was picked (LPN-68): this window shows [replacement] from now on.
     *
     * Everything is replaced **except [WindowContent.BookmarkList.columns]**, for *Reset*'s
     * reason above — a starred filter says which rows to show, not how wide this window's
     * cards should be, and it is stored without a column count for the same reason.
     */
    fun onFilterReplaced(replacement: WindowContent.BookmarkList) {
        changeFilter(replacement.copy(columns = filter.columns))
    }

    /**
     * This window's filter, exactly as it stands.
     *
     * The live one, which is not always the stored one: hosts write the filter back lazily
     * (the web shell waits for the typing to pause), so anything that needs the filter
     * *now* rather than as of the last write asks here.
     */
    fun filter(): WindowContent.BookmarkList = filter

    // ── The two panels, and the tag panel's own state ────────────────────────
    //
    // **At most one of the two is up at a time**, which is why neither is a boolean the host
    // toggles for itself. They are anchored to buttons a few pixels apart, they both cover the
    // rows, and two of them open at once is two panels overlapping over the thing they filter.
    // One intent each, and each closes the other.

    /**
     * *Tags ▾* — the panel of chips, its search box, its sort and *Hide empty*.
     *
     * Closing takes the panel's *Find a tag…* needle and its *Show more* with it, because both
     * narrow a list that is only visible inside the panel: left set, they would go on hiding
     * chips from behind a closed panel with the controls that explain why no longer on screen.
     * A reopened panel is therefore the whole vocabulary again, which is what somebody
     * reopening it is asking for.
     *
     * The sort and *Hide empty* stay: both are orderings of a list that still has everything in
     * it, so neither can turn the panel into a puzzle.
     */
    fun onTagPanelToggled(open: Boolean) {
        tagPanelOpen = open
        if (open) viewPanelOpen = false else resetTagPanelNarrowing()
        publish()
    }

    /** *View ▾* — the panel of knobs that decide the order and shape of the rows. */
    fun onViewPanelToggled(open: Boolean) {
        viewPanelOpen = open
        if (open) {
            tagPanelOpen = false
            resetTagPanelNarrowing()
        }
        publish()
    }

    /** *Find a tag…* — narrows the chips shown, and changes no filter. */
    fun onRailQueryChanged(query: String) {
        railQuery = query
        railLimit = RAIL_PAGE
        publish()
    }

    /** *By count / A–Z / Recent* — one of [RailSorts]. */
    fun onRailSortChanged(sort: String) {
        railSort = sort
        railLimit = RAIL_PAGE
        publish()
    }

    /** *Hide empty* — drop the chips nothing in this filter carries. */
    fun onHideEmptyChanged(hide: Boolean) {
        hideEmpty = hide
        railLimit = RAIL_PAGE
        publish()
    }

    /** *Show 48 more* — one more page of chips, and no other change. */
    fun onRailMoreRequested() {
        railLimit += RAIL_PAGE
        publish()
    }

    /** Put the panel's needle and its page count back, for a panel nobody is looking at. */
    private fun resetTagPanelNarrowing() {
        railQuery = ""
        railLimit = RAIL_PAGE
    }

    // ── Reading ──────────────────────────────────────────────────────────────

    /**
     * `space`, or the caret: open this row **in place**.
     *
     * In place is the whole design of it. There is no preview pane, so reading a summary
     * must not cost the reader their position in the list and must not take the window's
     * width away from the rows. Expanding is therefore a taller row, and collapsing puts
     * everything back exactly where it was.
     */
    fun onRowExpandToggled(id: Long) {
        expandedIds = if (id in expandedIds) expandedIds - id else expandedIds + id
        // Expanding a row is also focusing it: the keyboard and the mouse must not end up
        // with two different ideas of which row is current.
        focusedId = id
        publish()
    }

    /** A row was clicked, or reached some other way. */
    fun onRowFocused(id: Long) {
        if (focusedId == id) return
        focusedId = id
        publish()
    }

    /**
     * `j` (+1) and `k` (-1).
     *
     * From nothing, either key lands on the first row — pressing `k` in a window you have
     * not touched should put you *somewhere*, and the top is the only place that does not
     * require guessing what the reader meant. Clamped at both ends rather than wrapping: a
     * list that jumps from the bottom back to the top loses the reader completely.
     */
    fun onFocusMoved(delta: Int) {
        val rows = visibleBookmarks()
        if (rows.isEmpty()) return
        val current = rows.indexOfFirst { it.id == focusedId }
        val next = if (current < 0) 0 else (current + delta).coerceIn(0, rows.lastIndex)
        focusedId = rows[next].id
        publish()
    }

    /** The focused bookmark, or null — what `e` opens and what a row action acts on. */
    fun focusedBookmarkId(): Long? = focusedId

    /**
     * *Try again* on a row whose summary did not arrive.
     *
     * The only write a list window makes. It is here rather than being an intent the host
     * turns into a request because the answer has to land back in *this* window: the row
     * goes to `pending`, which arms the timer, which is what makes the summary appear
     * without anybody pressing anything else.
     *
     * A refusal — no key on this deployment, or both AI switches off for this account —
     * comes back as the server's own sentence over the rows, which stay exactly where they
     * are. That sentence is the entire reason those routes refuse instead of quietly
     * queueing work nothing will run, so it is shown rather than swallowed.
     *
     * A 404 is left as an ordinary message too. It means the bookmark went while the list
     * was open, and the refresh that follows removes the row — which says it better than a
     * state of its own would.
     */
    fun onTryAgainRequested(id: Long) {
        scope.launch {
            try {
                storage.summariseBookmark(id)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failed: Exception) {
                // The sentence, and **no refresh** — deliberately, because a fetch would
                // succeed and clear it a moment later, leaving a button that did nothing
                // and said nothing. Nothing was queued, so the rows on screen are still
                // exactly right; the next ordinary refresh clears the message.
                failure = failed.readableMessage(COULD_NOT_ASK)
                publish()
                return@launch
            }
            // Ask again, which is what picks up `pending` and arms the timer — and is
            // therefore the whole of what makes the summary appear without a second press.
            refreshes.value += 1
        }
    }

    // ── Internals ────────────────────────────────────────────────────────────

    private fun changeFilter(next: WindowContent.BookmarkList) {
        if (next == filter) return
        filter = next
        publish()
        onFilterChanged(next)
    }

    /** The result set, in order, ignoring grouping. */
    private fun visibleBookmarks(): List<Bookmark> =
        filterBookmarks(collection, filter, now())

    /**
     * Recompute the whole state and emit it.
     *
     * Everything derived is derived *here*, once, from the four or five things this model
     * actually holds — so there is no cached result set that can disagree with the filter
     * that produced it, and the view gets one object it renders without deciding anything.
     * The collection is one person's bookmarks held in memory; recomputing it on a
     * keystroke is arithmetic, not work.
     */
    private fun publish() {
        val failed = failure
        if (failed != null && !hasLoaded) {
            _stateFlow.value = State(failed = State.Failed(failed))
            return
        }
        if (!hasLoaded) {
            _stateFlow.value = State(loading = State.Loading)
            return
        }

        val instant = now()
        val result = filterBookmarks(collection, filter, instant)
        val resultIds = result.map { it.id }.toSet()
        // The timer's arming switch, recomputed against the result set on every publish —
        // which is also every fetch, every filter change and every keystroke. That is what
        // makes "only while a *visible* row is waiting" true rather than approximately
        // true: narrowing the filter past the last pending row disarms it immediately.
        val stillWaiting = result.any { it.isAwaitingSummary }
        awaiting.value = stillWaiting
        // A row that has left the result set cannot stay focused: `j` would move from a
        // row that is not on screen, which reads as the key doing nothing.
        if (focusedId != null && focusedId !in resultIds) focusedId = null

        val groups = when (filter.group) {
            BookmarkGroupings.NONE -> listOf(
                BookmarkDayGroup(dayStartMillis = 0, daysAgo = -1, bookmarks = result),
            )

            else -> groupBookmarksByDay(result, instant, zoneOffsetMillis())
        }

        val everyTag = railTags(result, vocabulary.map { it.tag }, liftedCounts(), quickTags.toSet())
        _stateFlow.value = State(
            ready = State.Ready(
                filter = filter,
                sentence = filterSummary(filter, FilterSummaryForm.LONG),
                groups = groups,
                isGrouped = filter.group != BookmarkGroupings.NONE,
                shown = result.size,
                total = collection.size,
                rail = rail(everyTag),
                railTotal = everyTag.size,
                railLimit = railLimit,
                knownTags = vocabulary,
                expandedIds = expandedIds.intersect(resultIds),
                focusedId = focusedId,
                tagPanelOpen = tagPanelOpen,
                viewPanelOpen = viewPanelOpen,
                railQuery = railQuery,
                railSort = railSort,
                hideEmpty = hideEmpty,
                isDirty = filter != WindowContent.BookmarkList(columns = filter.columns),
                loadedAt = loadedAt,
                isRefreshing = isRefreshing,
                isAwaitingSummaries = stillWaiting,
                message = failure,
            ),
        )
    }

    /**
     * The rail, ordered and narrowed as the panel's own controls ask.
     *
     * The active chips are **not** dropped by *Hide empty* or by the rail's search box:
     * they are the filter, they are pinned to the top of the panel with an `×` each, and a
     * control that could hide the thing you have to press to undo the filter is a control
     * that makes the filter a mystery.
     *
     * ── The account's chosen tags lead, in every sort ────────────────────────
     *
     * [quickTags] goes first and the rest follows, which is a promotion **on top of** the
     * chosen sort rather than a fourth sort beside *By count*, *A–Z* and *Recent*. Those
     * three answer "how should this list be arranged"; this answers "which of these did I
     * say I care about", and the two compose — everything under the promoted tags is in the
     * chosen order.
     *
     * ── …and among themselves they keep the order they were nominated in ─────
     *
     * The promoted half is ordered by its position in [quickTags], **not** by the rail's
     * sort. This is the one place the sort does not reach, and that is the point: the other
     * three orders are derived facts about the collection — how many rows carry a tag, how
     * recently, how it spells — and a shortlist is not a derived fact. It is a list somebody
     * wrote, in the order they wrote it, and the settings window is where they wrote it.
     * Re-sorting it by count would answer a question they did not ask and would move the
     * chips under their hand as the collection grew, which is exactly what nominating them
     * was meant to stop.
     *
     * Every pinned chip is in [quickTags] by construction — `pinned` is built from it — so
     * `indexOf` always finds one, and the promoted half is as totally ordered as the list
     * the account keeps.
     *
     * ── …and they are always drawn, including at zero ────────────────────────
     *
     * A pinned chip survives *Hide empty*, and [railTags] is handed the list so that a tag
     * **nothing has ever carried** gets a chip at all — the vocabulary would not name one,
     * because the vocabulary is the tags in use. The web view has the other half: its
     * collapsed two-row rail otherwise draws only tags the result set carries, and it makes
     * the same exception.
     *
     * That is the request read literally, and it is right. A shortlist is a claim about
     * where you intend to file things, not a report on where you have; `#read-later` is
     * most worth pressing on the day nothing is filed under it, and a chip that disappears
     * exactly then is a chip that is never there when it is needed. The three chips it
     * costs are chips somebody asked for by name.
     *
     * The rail's **search box still applies**, and that is not an inconsistency. *Hide
     * empty* is a standing preference about clutter, which a nomination overrides; typing
     * in *Find a tag…* is somebody looking for one particular tag right now, and a result
     * list that kept answering with tags they did not type would be a broken search.
     */
    private fun rail(everyTag: List<RailTag>): List<RailTag> {
        val active = filter.includeTags + filter.excludeTags
        val needle = railQuery.trim().trimStart('#').lowercase()
        return sortRailTags(everyTag, railSort)
            // `partition` keeps each half in the order the sort left it, so this is one
            // stable cut rather than a second comparator to keep in step with the first —
            // and then the chosen half is put back into the account's own order, which is
            // the one thing here the rail's sort is not allowed to decide.
            .partition { it.pinned }
            .let { (chosen, rest) -> chosen.sortedBy { quickTags.indexOf(it.tag) } + rest }
            .filter { it.tag in active || it.pinned || !hideEmpty || it.uses > 0 }
            .filter { it.tag in active || needle.isEmpty() || it.tag.contains(needle) }
    }

    /**
     * What each excluded tag is holding back, counted against the result set with its own
     * exclusion lifted.
     *
     * See [railTags]: the plain count of an excluded tag is zero by construction, and zero
     * beside a struck-through name says nothing at all. `#gaming 12` says what pressing it
     * again would bring back.
     */
    private fun liftedCounts(): Map<String, Int> = filter.excludeTags.associateWith { tag ->
        filterBookmarks(collection, filter.copy(excludeTags = filter.excludeTags - tag), now())
            .count { tag in it.tags }
    }

    /**
     * Exactly one field is non-null.
     *
     * The nullable-payload shape from §06 rather than a sealed hierarchy, so a Swift
     * view reads it as an `if let` chain rather than a cast per branch.
     *
     * @property loading the first request is open. The first state of every window.
     * @property ready the collection arrived. **May be empty**, and that is a
     *   different fact from [failed] — an account with no bookmarks is a normal
     *   account, and the view says so in words rather than showing nothing. A *refresh*
     *   that fails also lands here, with [Ready.message] set, because the rows on screen
     *   are still real.
     * @property failed the first request did not arrive, with a sentence to show.
     */
    data class State(
        val loading: Loading? = null,
        val ready: Ready? = null,
        val failed: Failed? = null,
    ) {
        data object Loading

        /**
         * A window, painted.
         *
         * Everything a surface needs and nothing it has to work out, which is what lets an
         * Android or iOS list render the same window from the same flow.
         *
         * @property filter the saved filter, for the knobs to draw themselves from.
         * @property sentence the filter in plain words — [FilterSummaryForm.LONG]. The
         *   only place the include/exclude logic is stated plainly, which is what keeps a
         *   filter from being modal state you have to undo to understand.
         * @property groups the result set, cut into days. Exactly one group, with
         *   [BookmarkDayGroup.daysAgo] of -1, when grouping is off — so a view walks one
         *   shape rather than branching.
         * @property isGrouped whether those groups are days worth drawing headers for.
         * @property shown how many rows survived the filter.
         * @property total how many the account has, for the footer's sense of proportion.
         * @property rail the tag chips, **counted against the result set** and already in
         *   the panel's chosen order, with the tags this account nominated in settings at
         *   the front of it. Already narrowed by *Find a tag…* and *Hide empty*; **not**
         *   truncated by [railLimit], which is a claim about how many of them a panel draws
         *   and therefore the panel's to apply. A phone's filter sheet draws this list as it
         *   is given.
         * @property railTotal how many chips there would be with nothing narrowed — the
         *   whole vocabulary plus whatever this account nominated. The denominator in the tag
         *   panel's `48 OF 214 TAGS`, which is how the truncation gets stated rather than
         *   hidden.
         * @property railLimit how many of the *inactive* chips a panel may draw before
         *   *Show more*. See the field of the same name.
         * @property knownTags the account's vocabulary, for the search field's `#tag` menu.
         * @property expandedIds which rows are open, narrowed to rows that are on screen.
         * @property focusedId where `j`/`k` has got to.
         * @property tagPanelOpen the tag panel is up, over the rows.
         * @property viewPanelOpen the *View* panel is up. Never at the same time as
         *   [tagPanelOpen] — the two intents that set them see to that.
         * @property isDirty this window differs from a brand new one in some way that
         *   changes which rows it shows or how they are ordered. What *Reset* is offered for,
         *   and the reason it is not offered otherwise: a control that undoes nothing is a
         *   control somebody presses to find out what it does.
         * @property loadedAt when the collection arrived, epoch millis; 0 before it has.
         * @property isRefreshing a re-fetch is open behind rows that are still real.
         * @property isAwaitingSummaries at least one row on screen is waiting for its
         *   summary, so this window is asking again every few seconds until none is. A
         *   view may say so; the reason it is published at all is that a timer nothing can
         *   observe is a timer nothing can be asserted about.
         * @property message a sentence about a *refresh* that failed. Null usually.
         */
        data class Ready(
            val filter: WindowContent.BookmarkList = WindowContent.BookmarkList(),
            val sentence: String = UNFILTERED,
            val groups: List<BookmarkDayGroup> = emptyList(),
            val isGrouped: Boolean = false,
            val shown: Int = 0,
            val total: Int = 0,
            val rail: List<RailTag> = emptyList(),
            val railTotal: Int = 0,
            val railLimit: Int = RAIL_PAGE,
            val knownTags: List<TagUse> = emptyList(),
            val expandedIds: Set<Long> = emptySet(),
            val focusedId: Long? = null,
            val tagPanelOpen: Boolean = false,
            val viewPanelOpen: Boolean = false,
            val railQuery: String = "",
            val railSort: String = RailSorts.COUNT,
            val hideEmpty: Boolean = false,
            val isDirty: Boolean = false,
            val loadedAt: Long = 0,
            val isRefreshing: Boolean = false,
            val isAwaitingSummaries: Boolean = false,
            val message: String? = null,
        ) {
            /** Every row on screen, in order, with the day headers flattened away. */
            val bookmarks: List<Bookmark> get() = groups.flatMap { it.bookmarks }

            /** Which of the three states this tag's chip is in. */
            fun stateOf(tag: String): TagChipState = when (tag) {
                in filter.includeTags -> TagChipState.INCLUDED
                in filter.excludeTags -> TagChipState.EXCLUDED
                else -> TagChipState.OFF
            }
        }

        data class Failed(val message: String)
    }

    /**
     * The three states of a rail chip.
     *
     * Three, times two origins — a tag the model coined reads differently — which is
     * exactly six treatments if the origin were a colour. It is a mark instead, so it
     * stays at three. `TagInput`'s `CommittedTag.fromAi` draws the same mark, so the
     * list and the editor agree about what an AI tag looks like.
     */
    enum class TagChipState { OFF, INCLUDED, EXCLUDED }

    private companion object {
        /** Only reached when the failure carried no sentence of its own. */
        const val COULD_NOT_LOAD = "Could not load your bookmarks."

        /** Likewise — the summarise routes' own refusals are sentences already. */
        const val COULD_NOT_ASK = "Could not ask for a summary."
    }
}

/**
 * Whether this bookmark's summary is still on its way.
 *
 * `pending` and `running` are one fact to everything that draws a row — the queue's
 * internal difference between "waiting" and "somebody has it" is the worker's business —
 * so they are folded here, once, rather than at each of the three places that ask: the
 * badge, the skeleton lines, and the timer's arming switch.
 */
val Bookmark.isAwaitingSummary: Boolean
    get() = aiState == AiStates.PENDING || aiState == AiStates.RUNNING
